/**
 * Host-declared path-exclusion matcher for the CMP loader.
 *
 * The host page may set `window.__cmpConfig.excludedPaths` to an array of
 * regular-expression source strings. If any of them matches the current
 * `location.pathname`, the loader must NOT load the CMP on that page.
 *
 * This is the ONE place the CMP legitimately reads the URL — kept isolated,
 * pure and DOM-free (the pathname is passed in) so it stays testable without
 * stubbing `location`. Mirrors the loud-but-safe validation style of
 * `readScripts` in src/config/runtimeConfig.ts.
 *
 * Contract:
 *   - Patterns are compiled with `new RegExp(entry)` and tested with
 *     `.test(pathname)` — UNANCHORED. Hosts use `^`/`$` for exact matches.
 *   - DEFAULT-OPEN: exclusion is strictly opt-in. Missing/undefined, a
 *     non-array value, an empty array, and an array whose entries are ALL
 *     invalid every resolve to `false` (load everywhere). Only a real, valid
 *     pattern that actually matches suppresses loading.
 *   - Never throws. Every invalid entry is skipped and logged via
 *     `console.warn`; absence (undefined) is silent (not a misconfig).
 */
export function shouldExcludePath(pathname: string, rawExcludedPaths: unknown): boolean {
  if (rawExcludedPaths === undefined) return false;

  if (!Array.isArray(rawExcludedPaths)) {
    console.warn(
      '[CMP loader] window.__cmpConfig.excludedPaths is not an array — ignoring it (CMP loads on all paths).',
    );
    return false;
  }

  return rawExcludedPaths.some((entry) => matchesEntry(pathname, entry));
}

/** Validates a single entry and tests it against `pathname`. Never throws. */
function matchesEntry(pathname: string, entry: unknown): boolean {
  if (typeof entry !== 'string') {
    console.warn(
      '[CMP loader] window.__cmpConfig.excludedPaths contains a non-string entry — ignoring it.',
    );
    return false;
  }

  // An empty / whitespace-only source compiles to a match-everything RegExp,
  // which would silently exclude the CMP from every page — treat as a mistake.
  if (entry.trim() === '') {
    console.warn(
      '[CMP loader] window.__cmpConfig.excludedPaths contains an empty pattern — ignoring it.',
    );
    return false;
  }

  try {
    return new RegExp(entry).test(pathname);
  } catch {
    console.warn(
      `[CMP loader] window.__cmpConfig.excludedPaths contains an invalid regular expression: "${entry}" — ignoring it.`,
    );
    return false;
  }
}
